import { useRef, useState } from 'react';
import { CameraAlt as CameraAltIcon } from '@mui/icons-material';
import {
  Button,
  enqueueSnackbar,
  fullScreenButtonClick,
  ImageGallery,
  ImageGalleryCard,
  ImageGalleryCardWrapper,
  Input,
  InputContainer,
  SearchableSelect,
  Select,
  Typography,
  YardPlaceSelects,
} from '../../design-system';
import {
  DashbaordFormFooter,
  DashboardAutoColumnInputs,
  DashboardInputs,
  DashboardWrapper,
  HideInput,
} from '../dashboard';
import { useParams } from 'react-router-dom';
import {
  useGetContainerOcrMutation,
  useItemCheckInventoryMutation,
  useItemUploadImagesMutation,
} from '../../hooks';
import { inventoryService } from '../../services';
import { extendContainerValidator, filesFormatValidator } from '../../utils';
import { containerTypes, inventoryItemStatusLabel, MESSAGES, TABLE } from '../../const';
import { useImmer } from 'use-immer';

export const InventoryCheck = () => {
  const { id: inventoryId = '' } = useParams();
  const { mutate: getContainerOcr, isLoading: isOcrLoading } = useGetContainerOcrMutation();
  const { mutate } = useItemCheckInventoryMutation();
  const itemUploadImagesMutation = useItemUploadImagesMutation();

  const containerId = useRef('');
  const [containerNumber, setContainerNumber] = useState('');
  const [block, setBlock] = useState('');
  const [row, setRow] = useState('');
  const [slot, setSlot] = useState('');
  const [tier, setTier] = useState('');
  const [type, setType] = useState('');
  const [status, setStatus] = useState('');

  const [images, setImages] = useImmer<any[]>([]);
  const [imagesFiles, setImagesFiles] = useImmer<any[]>([]);
  const ocrImageInputRef = useRef<any>();

  const resetForm = () => {
    containerId.current = '';
    setContainerNumber('');
    setBlock('');
    setRow('');
    setSlot('');
    setTier('');
    setType('');
    setStatus('');

    setImages([]);
    setImagesFiles([]);

    ocrImageInputRef.current.type = 'text';
    ocrImageInputRef.current.type = 'file';
  };

  const handleContainerSelect = (option: any, changeImage = true) => {
    if (containerId.current === option.id) return;
    if (option.isWrong) return;

    containerId.current = option.id !== 'fakeID' ? option.id : '';
    setContainerNumber(option.value);

    if (containerId.current === '' && option.status === 'NOT_FOUND') {
      enqueueSnackbar(MESSAGES.INVENTORY.WARNING.NOT_FOUND, 'warning');
    }

    if (option.checked?.image && imagesFiles.length) {
      setImagesFiles([]);
    }

    const newBlock = option.checked ? option.checked.block : option.unchecked.block;
    const newRow = option.checked ? option.checked.row : option.unchecked.row;
    const newSlot = option.checked ? option.checked.slot : option.unchecked.slot;
    const newTier = option.checked ? option.checked.tier : option.unchecked.tier;
    const newType = option.checked ? option.checked.type : option.unchecked.type;
    const newImages = option.checked
      ? option.checked.image.map(({ url }: any) => url) || []
      : imagesFiles.length
      ? images
      : [];

    setBlock(newBlock || '');
    setRow(newRow != null && newRow !== '' ? String(newRow) : '');
    setSlot(newSlot != null && newSlot !== '' ? String(newSlot) : '');
    setTier(newTier != null && newTier !== '' ? String(newTier) : '');
    setType(newType || '');
    if (changeImage) setImages(newImages);
    setStatus(option.status);
  };

  const handleZoomImageClick = () => {
    fullScreenButtonClick();
  };

  const handleChangeImages = (newImages: any, newFiles: any) => {
    if (filesFormatValidator(newFiles, 'Image')) {
      enqueueSnackbar(filesFormatValidator(newFiles, 'Image'), 'warning');
      return;
    }

    setImages([newImages[0]]);
    setImagesFiles([newFiles[0]]);

    getContainerOcr(newFiles[0], {
      onSuccess: ({ data }: any) => {
        if (!data.number) return;

        setContainerNumber(data.number);
        setType(data.type);
        setStatus('NOT_FOUND');

        inventoryService
          .getItems({
            id: inventoryId,
            number: data.number,
            take: 1,
            skip: 0,
          })()
          .then((options) => {
            handleContainerSelect(
              {
                ...options[0],
                value: data.number,
                unchecked: { ...options[0].unchecked, type: data.type },
              },
              false,
            );
          });
      },
    });

    ocrImageInputRef.current.type = 'text';
    ocrImageInputRef.current.type = 'file';
  };

  const handleSubmitClick = () => {
    const itemUploadImagesMutate = (id: string) =>
      itemUploadImagesMutation.mutateAsync({
        id,
        photos: imagesFiles.map((file) => ({
          action: 'upload',
          file,
        })),
      });

    mutate(
      {
        id: inventoryId,
        containerId: containerId.current,
        number: containerNumber,
        type,
        block,
        row,
        ...(slot ? { slot } : {}),
        ...(tier ? { tier } : {}),
      },
      {
        onSuccess: async (ids: any) => {
          await itemUploadImagesMutate(ids[0]);

          enqueueSnackbar(MESSAGES.INVENTORY.SUCCESS.CHECKED, 'success');
          resetForm();
        },
        onError: () => {
          enqueueSnackbar(MESSAGES.INVENTORY.ERROR.CHECKED, 'error');
        },
      },
    );
  };

  return (
    <>
      <DashboardWrapper>
        <Typography variant={'textXL'} weight={'bold'}>
          Inventory{containerNumber ? `: ${inventoryItemStatusLabel[status]}` : ''}
        </Typography>
        <DashboardInputs>
          <DashboardInputs
            columns={process.env.REACT_APP_TEZPORT_API_URL ? '1fr max-content' : '1fr'}
          >
            <SearchableSelect
              InputComponent={InputContainer}
              label={'Container Number'}
              selectedOptionLabel={containerNumber || 'Select Container Number'}
              isLoading={isOcrLoading}
              onSelect={handleContainerSelect}
              inputValueKey={(inputValue: string) =>
                extendContainerValidator(inputValue) && inputValue.length >= 3 ? inputValue : ''
              }
              queryKey={['inventory', 'items', inventoryId]}
              queryFn={(number) =>
                inventoryService.getItems({
                  id: inventoryId,
                  ...(extendContainerValidator(number) && number.length >= 3 ? { number } : {}),
                  take: TABLE.PAGE_MAX_SIZE,
                  skip: 0,
                  notLoading: true,
                })
              }
              selectedAreasEqaul={{ key: 'id', keys: [containerId.current] }}
            />
            <Button
              className={'row-button-square'}
              onClick={() => ocrImageInputRef.current.click()}
              isSquare
            >
              <CameraAltIcon />
            </Button>
          </DashboardInputs>
          <DashboardAutoColumnInputs>
            <YardPlaceSelects
              inventoryId={inventoryId}
              block={block}
              row={row}
              slot={slot}
              tier={tier}
              setBlock={setBlock}
              setRow={setRow}
              setSlot={setSlot}
              setTier={setTier}
              isLoading={isOcrLoading}
              withDefaultValues
            />
          </DashboardAutoColumnInputs>
          <Select
            label={'Container Type'}
            placeholder={'Select Container Type'}
            isLoading={isOcrLoading}
            options={containerTypes}
            value={type}
            onSelect={setType}
          />
          <Typography variant={'textM'} weight={'bold'} color={'--gray-100'}>
            Images*
          </Typography>
          {!!images.length && (
            <ImageGalleryCardWrapper>
              {images.map((img: any, i: any) => (
                <ImageGalleryCard
                  key={i}
                  img={img.url || img}
                  handleZoomClick={handleZoomImageClick}
                  handleDeleteImageClick={() => {}}
                  hideDeleteButton
                />
              ))}
            </ImageGalleryCardWrapper>
          )}
        </DashboardInputs>
        <HideInput>
          <Input
            id={'ocr-image'}
            label={'OCR Image'}
            data-size={'medium'}
            type={'file'}
            inputRef={ocrImageInputRef}
            inputProps={{
              accept: '.jpeg, .jpg, .png, .webp',
              capture: 'environment',
            }}
            value={undefined}
            handleChangeFiles={handleChangeImages}
          />
        </HideInput>
        <DashbaordFormFooter>
          <Button
            size={'medium'}
            onClick={handleSubmitClick}
            disabled={
              !containerNumber || !block || !row || !type || !tier || !images.length || isOcrLoading
            }
          >
            {!containerNumber || containerId.current || status === 'EXTRA_FOUND'
              ? 'Check'
              : 'New Container'}
          </Button>
        </DashbaordFormFooter>
      </DashboardWrapper>
      <ImageGallery photos={images} startIndex={0} />
    </>
  );
};
